import { zodResolver } from '@hookform/resolvers/zod';
import { Slider } from '@miblanchard/react-native-slider';
import { Image } from 'expo-image';
import { useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { View } from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';
import { z } from 'zod';

import type { Car } from '@/api/cars/types';
import { formatNumber, getCarImages, isDesktop } from '@/core';
import { GREY_10, GREY_30, GREY_60, PINKLING } from '@/style/colors';
import { Card, ControlledInput, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { ChevronLeft } from '@/ui/icons/chevron-left';
import { ChevronRight } from '@/ui/icons/chevron-right';
import Toggle from '@/ui/toggle';

function interpolate(data: Record<number, number>, x: number): number | null {
  const xs = Object.keys(data)
    .map(Number)
    .sort((a, b) => a - b);

  for (let i = 0; i < xs.length - 1; i++) {
    const x1 = xs[i];
    const x2 = xs[i + 1];

    if (x >= x1 && x <= x2) {
      const y1 = data[x1];
      const y2 = data[x2];

      return y1 + (y2 - y1) * ((x - x1) / (x2 - x1));
    }
  }

  return null;
}

type CarPricingFormProps = {
  // dailyRate: number;
  // oneDayMultiplier?: number;
  // defaultLocationId?: number;
};

const carPricingFormSchema = z.object({
  useAutomatedPricing: z.boolean(),

  // manual pricing
  dailyRate: z.string().optional(),

  // automated pricing
  minimumDailyRate: z.string().optional(),
});

function createPricingFormSchema(modelName: string) {
  return carPricingFormSchema.superRefine((data, ctx) => {
    /* ----------------------------
     * Required fields by mode
     * ---------------------------- */
    if (data.useAutomatedPricing) {
      if (!data.minimumDailyRate) {
        ctx.addIssue({
          path: ['minimumDailyRate'],
          message: 'Required',
          code: z.ZodIssueCode.custom,
        });
      }
    } else {
      if (!data.dailyRate) {
        ctx.addIssue({
          path: ['dailyRate'],
          message: 'Required',
          code: z.ZodIssueCode.custom,
        });
      }
    }
  });
}

export type CarPricingFormType = z.infer<typeof carPricingFormSchema>;

export default function CarPricingForm({
  car,
  index,
  projectedMonthlyIncome,
  onChange,
  onValidChange,
  defaultValues,
  style,
}: {
  car: Car;
  index: number;
  projectedMonthlyIncome: any;
  onChange: (index: number, values: CarPricingFormType) => void;
  onValidChange: (index: number, isValid: boolean) => void;
  defaultValues?: Partial<CarPricingFormType>;
  style?: any;
}) {
  const { styles, theme } = useStyles(stylesheet);

  const schema = useMemo(
    () => createPricingFormSchema(car.modelName),
    [car.modelName]
  );

  const {
    control,
    trigger,
    setValue,
    getValues,
    formState: { isValid, errors },
    watch,
  } = useForm<CarPricingFormType>({
    defaultValues: {
      useAutomatedPricing:
        defaultValues?.useAutomatedPricing ?? car.useAutomatedPricing,
      dailyRate: defaultValues?.dailyRate ?? car.dailyRate?.toString(),
      minimumDailyRate:
        defaultValues?.minimumDailyRate ?? car.minimumDailyRate?.toString(),
    },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    onChange(index, getValues());
    trigger();
  }, []);

  useEffect(() => {
    onValidChange(index, isValid);
  }, [isValid]);

  useEffect(() => {
    const sub = watch(() => {
      onChange(index, getValues());
    });
    return () => sub.unsubscribe();
  }, [watch]);

  const [dailyRate, useAutomatedPricing, minimumDailyRate] = useWatch({
    control,
    name: ['dailyRate', 'useAutomatedPricing', 'minimumDailyRate'],
  });

  useEffect(() => {
    if (projectedMonthlyIncome && !minimumDailyRate) {
      setValue(
        'minimumDailyRate',
        projectedMonthlyIncome.defaultRate.toString()
      );
    }
  }, [projectedMonthlyIncome, minimumDailyRate]);

  if (!projectedMonthlyIncome) {
    return null;
  }

  return (
    <Card style={[styles.container, style]}>
      <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
        <Image
          source={{
            uri:
              car && getCarImages(car.images).length > 0
                ? getCarImages(car.images)[0].url
                : '',
          }}
          style={{ width: 70, height: 48 }}
          contentFit="cover"
        />

        <View>
          <Text type="bodyRegular">
            {car.vehicleYear && car.brandName && car.modelName
              ? `${car.vehicleYear} ${car.brandName} ${car.modelName}`
              : 'Loading Car Information...'}
          </Text>
          <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
            {car.vin}
          </Text>
        </View>
      </View>

      {useAutomatedPricing === true ? (
        <View
          style={[
            {
              padding: 16,
              gap: 4,
            },
            !isDesktop() && {
              borderRadius: 16,
              borderWidth: 1,
              borderColor: theme.colors.card.default.borderColor,
            },
          ]}
        >
          <Text type="caption1">Projected Monthly Income</Text>

          {minimumDailyRate ? (
            <Text type="title1">
              $
              {Math.round(
                interpolate(
                  projectedMonthlyIncome.rateToEarnings,
                  minimumDailyRate?.toString()
                )
              )}
            </Text>
          ) : null}
        </View>
      ) : (
        <View
          style={[
            {
              padding: 16,
              gap: 4,
            },
            !isDesktop() && {
              borderRadius: 16,
              borderWidth: 1,
              borderColor: theme.colors.card.default.borderColor,
            },
          ]}
        >
          {isDesktop() ? (
            <Text type="caption1">Your take-home rate (70% of daily rate)</Text>
          ) : (
            <Text type="caption1">Your take-home rate</Text>
          )}

          {minimumDailyRate ? (
            <Text type="title1">${formatNumber(dailyRate * 0.7)}</Text>
          ) : null}

          {!isDesktop() ? (
            <Text
              type="caption1"
              style={{ color: theme.colors.text.secondary }}
            >
              70% of daily rate
            </Text>
          ) : null}
        </View>
      )}

      <View style={{ flex: 1, gap: 16 }}>
        {useAutomatedPricing === false ? (
          <View
            style={{
              height: '100%',
              justifyContent: 'center',
              alignItems: isDesktop() ? 'flex-start' : 'stretch',
            }}
          >
            <View style={styles.dailyRateInputWrapper}>
              <ControlledInput
                control={control}
                name="dailyRate"
                keyboardType="number-pad"
                onBlur={() => {
                  trigger('dailyRate');
                }}
                onChangeText={(text) => {
                  const numeric = text
                    .replace(/[^0-9.]/g, '')
                    .replace(/(\..*)\./g, '$1');
                  setValue('dailyRate', numeric);
                  trigger('dailyRate');
                }}
                tooltipText="This will be the daily rate your car earns whenever on a trip. You'll keep 70% of the daily rate. Weekly and monthly trips are automatically discounted by 15% and 30% respectively."
              />
            </View>
          </View>
        ) : null}

        {useAutomatedPricing === true ? (
          <View style={styles.automatedPricingContainer}>
            {!isDesktop() ? (
              <View>
                <Text type="bodyRegular">Adjust your minimum daily rate</Text>
              </View>
            ) : null}

            <View style={styles.sliderContainer}>
              <Slider
                value={minimumDailyRate ? Number(minimumDailyRate) : 0}
                onSlidingComplete={(values) => {
                  setValue('minimumDailyRate', String(values[0]));
                  trigger('minimumDailyRate');
                }}
                onValueChange={(values) => {
                  setValue('minimumDailyRate', String(values[0]));
                  trigger('minimumDailyRate');
                }}
                minimumValue={projectedMonthlyIncome.minimumRate}
                maximumValue={projectedMonthlyIncome.maximumRate}
                step={1}
                minimumTrackTintColor={PINKLING}
                maximumTrackTintColor={
                  UnistylesRuntime.themeName === 'dark' ? GREY_60 : GREY_30
                }
                thumbTintColor={
                  UnistylesRuntime.themeName === 'dark' ? GREY_10 : GREY_60
                }
                renderThumbComponent={(idx: number) => {
                  return (
                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: theme.colors.toggle.default.handle,
                        borderRadius: 16,
                        padding: 8,
                        width: 50,
                        borderColor: theme.colors.card.default.borderColor,
                        borderWidth: 1,
                      }}
                    >
                      <Icon
                        type="naked"
                        icon={<ChevronLeft />}
                        fillColor={theme.colors.icon.color}
                        width={10}
                        height={10}
                      />

                      <Icon
                        type="naked"
                        icon={<ChevronRight />}
                        fillColor={theme.colors.icon.color}
                        width={10}
                        height={10}
                      />
                    </View>
                  );
                }}
                renderAboveThumbComponent={() => {
                  return (
                    <View
                      style={{
                        flexDirection: 'row',
                        gap: 4,
                        position: 'absolute',
                        left: -40,
                        top: -20,
                      }}
                    >
                      <Text
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      >
                        Min
                      </Text>

                      <Text type="bodyRegular">
                        {`$${minimumDailyRate}${
                          minimumDailyRate ===
                          String(projectedMonthlyIncome.maximumRate)
                            ? ' +'
                            : ''
                        }/day`}
                      </Text>
                    </View>
                  );
                }}
                trackMarks={[projectedMonthlyIncome.defaultRate]}
                renderTrackMarkComponent={(idx: number) => {
                  return (
                    <View>
                      <Text
                        type="caption1"
                        style={{
                          position: 'absolute',
                          width: 200,
                          bottom: -30,
                          left: -40,
                          color: theme.colors.text.secondary,
                        }}
                      >
                        Recommended minimum
                      </Text>

                      <View
                        style={{
                          width: 16,
                          height: 16,
                          backgroundColor:
                            theme.colors.card.default.borderColor,
                          borderWidth: 1,
                          borderColor: theme.colors.icon.brandColor,
                          borderRadius: 8,
                        }}
                      />
                    </View>
                  );
                }}
              />
            </View>
          </View>
        ) : null}
      </View>

      {isDesktop() ? (
        <View
          style={{
            padding: 16,
            gap: 4,
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text type="bodyRegular">Fixed daily rate</Text>

          <Toggle
            enabled={!useAutomatedPricing}
            onToggle={() =>
              setValue('useAutomatedPricing', !useAutomatedPricing)
            }
          />
        </View>
      ) : (
        <View
          style={{
            padding: 16,
            gap: 4,
            backgroundColor: theme.colors.card.hover.backgroundColor,
            borderWidth: 1,
            borderColor: theme.colors.card.hover.borderColor,
            borderRadius: 16,
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text type="bodyRegular">Fixed daily rate</Text>

          <Toggle
            enabled={!useAutomatedPricing}
            onToggle={() =>
              setValue('useAutomatedPricing', !useAutomatedPricing)
            }
          />
        </View>
      )}
    </Card>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    padding: 16,
    gap: 24,
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    backgroundColor: {
      xs: theme.colors.card.default.backgroundColor,
      md: 'transparent',
    },
    borderWidth: {
      xs: 1,
      md: 0,
    },
  },
  automatedPricingContainer: {
    height: '100%',
    gap: 16,
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    alignItems: {
      md: 'center',
    },
  },
  sliderContainer: {
    flex: 1,
    height: 70,
    marginTop: {
      xs: 16,
      md: 0,
    },
    marginBottom: {
      xs: 16,
      md: 0,
    },
  },
  dailyRateInputWrapper: {
    width: {
      xs: '100%',
      md: 160,
    },
  },
}));
